import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../Login/AuthContext';
import './styles/admin-dashboard.css';
import { useBranding } from '../Header/useBranding';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding();
  const { logout: authLogout } = useAuth();
  const [pendingEvents, setPendingEvents] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalArtists: 0,
    totalVenues: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [roleRequests, setRoleRequests] = useState([]);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  
  // ข้อมูลผู้ดูแลระบบจาก localStorage
  const admin = JSON.parse(localStorage.getItem('user') || '{}');
  
  useEffect(() => {
    // เรียกข้อมูลเมื่อโหลด Dashboard
    const fetchAdminData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // เรียกข้อมูลสำหรับผู้ดูแลระบบ
        const [pendingEventsRes, recentUsersRes, statsRes, roleRequestsRes, reportStatsRes] = await Promise.allSettled([
          axios.get('/api/events/pending'),
          axios.get('/api/admin/users/recent'),
          axios.get('/api/admin/stats'),
          axios.get('/api/admin/role-requests', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
          axios.get('/api/reports/stats', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        ]);
        
        // Handle pending events response
        if (pendingEventsRes.status === 'fulfilled' && pendingEventsRes.value?.data) {
          setPendingEvents(Array.isArray(pendingEventsRes.value.data) ? pendingEventsRes.value.data : []);
        } else {
          console.warn('Failed to fetch pending events:', pendingEventsRes.reason);
          setPendingEvents([]);
        }
        
        // Handle recent users response
        if (recentUsersRes.status === 'fulfilled' && recentUsersRes.value?.data) {
          setRecentUsers(Array.isArray(recentUsersRes.value.data) ? recentUsersRes.value.data : []);
        } else {
          console.warn('Failed to fetch recent users:', recentUsersRes.reason);
          setRecentUsers([]);
        }
        
        // Handle stats response
        if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
          setStats({
            totalEvents: statsRes.value.data.totalEvents || 0,
            totalArtists: statsRes.value.data.totalArtists || 0,
            totalVenues: statsRes.value.data.totalVenues || 0,
            activeUsers: statsRes.value.data.activeUsers || 0
          });
        } else {
          console.warn('Failed to fetch stats:', statsRes.reason);
        }

        // Handle role requests response
        if (roleRequestsRes.status === 'fulfilled' && roleRequestsRes.value?.data) {
          setRoleRequests(Array.isArray(roleRequestsRes.value.data) ? roleRequestsRes.value.data : []);
        } else {
          setRoleRequests([]);
        }

        // Handle report stats response
        if (reportStatsRes.status === 'fulfilled' && reportStatsRes.value?.data) {
          setPendingReportsCount(reportStatsRes.value.data.pending || 0);
        }

      } catch (error) {
        console.error('Error fetching admin data:', error);
        setError('Failed to load dashboard data');
        // Set default empty arrays to prevent map errors
        setPendingEvents([]);
        setRecentUsers([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdminData();
  }, []);
  
  // ฟังก์ชันสำหรับออกจากระบบ
  const handleLogout = async () => {
    try {
      // Use AuthContext's logout function which properly clears all auth state
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, navigate to login
      navigate('/login');
    }
  };

  // ฟังก์ชันสำหรับค้นหา
  const handleSearch = (e) => {
    e.preventDefault();
    // Handle search functionality
    console.log('Searching for:', searchQuery);
  };

  // ฟังก์ชันสำหรับอนุมัติกิจกรรม
  const handleApproveEvent = async (eventId) => {
    try {
      await axios.put(`/api/admin/events/${eventId}/approve`);
      // อัพเดตรายการหลังจากอนุมัติ
      setPendingEvents(prevEvents => 
        Array.isArray(prevEvents) ? prevEvents.filter(event => event.id !== eventId) : []
      );
    } catch (error) {
      console.error('Error approving event:', error);
    }
  };

  // ฟังก์ชันสำหรับปฏิเสธกิจกรรม
  const handleRejectEvent = async (eventId) => {
    try {
      await axios.put(`/api/admin/events/${eventId}/reject`);
      // อัพเดตรายการหลังจากปฏิเสธ
      setPendingEvents(prevEvents =>
        Array.isArray(prevEvents) ? prevEvents.filter(event => event.id !== eventId) : []
      );
    } catch (error) {
      console.error('Error rejecting event:', error);
    }
  };

  const handleApproveRoleRequest = async (id) => {
    try {
      await axios.put(`/api/admin/role-requests/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRoleRequests(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error approving role request:', error);
    }
  };

  const handleRejectRoleRequest = async (id) => {
    try {
      await axios.put(`/api/admin/role-requests/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRoleRequests(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error rejecting role request:', error);
    }
  };

  const roleLabel = (role) => {
    if (role === 'venue_owner') return 'เจ้าของธุรกิจ';
    if (role === 'artist') return 'ศิลปิน';
    return role;
  };

  // Show error state if there's an error
  if (error) {
    return (
      <div className="dashboard-wrapper">
        <div className="error-state">
          <h2>เกิดข้อผิดพลาด</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            <i className="fas fa-refresh"></i> โหลดใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      {/* Modern Admin Header */}
      <header className="admin-header">
        <div className="admin-header-container">
          {!branding.hide && (
            <div className="admin-branding">
              <a href="/admin/dashboard" className="admin-logo">
                <span>{branding.brand.name}</span>
              </a>
              <div className="admin-badge">Admin</div>
            </div>
          )}
          
          <div className="admin-search">
            <form onSubmit={handleSearch}>
              <i className="fas fa-search admin-search-icon"></i>
              <input 
                type="text" 
                className="admin-search-input" 
                placeholder="ค้นหากิจกรรม, ศิลปิน, ผู้ใช้..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="admin-search-button">
                <i className="fas fa-search"></i> ค้นหา
              </button>
            </form>
          </div>
          
          <div className="admin-user-section">
            <div className="admin-user">
              <div className="admin-avatar">
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(admin.name || 'Admin')}&background=e67e22&color=fff`}
                  alt={admin.name || 'Admin'}
                />
              </div>
              <div>
                <div className="admin-name">{admin.name || 'Admin'}</div>
                <div className="admin-role">ผู้ดูแลระบบ</div>
              </div>
            </div>
            
            <button className="admin-logout" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt admin-logout-icon"></i>
              ออกจากระบบ
            </button>
          </div>
        </div>
      </header>
      
      <div className="dashboard-content">
        <aside className="admin-sidebar">
          <nav className="admin-nav" translate="no">
            <h3><span>เมนูหลัก</span></h3>
            <ul>
              <li>
                <NavLink to="/admin/dashboard" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-home"></i><span> หน้าหลัก</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/eventinput" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-plus-circle"></i><span> เพิ่มกิจกรรม</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/approval" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-check-circle"></i><span> อนุมัติกิจกรรม</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/auto-approval" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-robot"></i><span> AI Auto-Approval</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/event-list" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-list"></i><span> รายการกิจกรรม</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/editpage" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-list"></i><span> แก้ไขกิจกรรม</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/event-upload" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-upload"></i><span> อัพโหลดข้อมูล</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/users" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-users"></i><span> จัดการผู้ใช้</span>
                  {roleRequests.length > 0 && <span className="badge" style={{ marginLeft: 6, background: '#e74c3c', color: '#fff', borderRadius: '50%', padding: '2px 6px', fontSize: '11px' }}>{roleRequests.length}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/artists" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-music"></i><span> จัดการศิลปิน</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/venues" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-map-marker-alt"></i><span> จัดการสถานที่</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/analytics" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-chart-line"></i><span> แดชบอร์ดวิเคราะห์</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/reports" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-chart-bar"></i><span> รายงาน</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/report-management" className={({isActive}) => isActive ? 'active' : ''}>
                  <i className="fas fa-flag"></i><span> จัดการรายงานผิดปกติ</span>
                  {pendingReportsCount > 0 && <span className="badge" style={{ marginLeft: 6, background: '#e74c3c', color: '#fff', borderRadius: '50%', padding: '2px 6px', fontSize: '11px' }}>{pendingReportsCount}</span>}
                </NavLink>
              </li>
              <li>
                <Link to="/tour-guide" state={{ background: location }} className={location.pathname === '/tour-guide' ? 'active' : ''}>
                  <i className="fas fa-route"></i><span> Tour Group</span>
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
        
        <main className="admin-main">
          <section className="admin-welcome">
            <h2>ระบบจัดการ {branding.brand?.name || 'EventZoom'}</h2>
            <p>ยินดีต้อนรับ, {admin.name || 'Admin'}! นี่คือศูนย์กลางการจัดการเว็บไซต์ของคุณ</p>
          </section>
          
          <section className="stats-cards">
            <div className="stat-card">
              <h3><i className="fas fa-calendar-alt"></i> กิจกรรมทั้งหมด</h3>
              <div className="stat-value">{stats.totalEvents}</div>
              <a href="/admin/events">จัดการกิจกรรม</a>
            </div>
            
            <div className="stat-card">
              <h3><i className="fas fa-music"></i> ศิลปินทั้งหมด</h3>
              <div className="stat-value">{stats.totalArtists}</div>
              <a href="/admin/artists">จัดการศิลปิน</a>
            </div>
            
            <div className="stat-card">
              <h3><i className="fas fa-map-marker-alt"></i> สถานที่ทั้งหมด</h3>
              <div className="stat-value">{stats.totalVenues}</div>
              <a href="/admin/venues">จัดการสถานที่</a>
            </div>
            
            <div className="stat-card">
              <h3><i className="fas fa-users"></i> ผู้ใช้ที่กำลังใช้งาน</h3>
              <div className="stat-value">{stats.activeUsers}</div>
              <a href="/admin/users">จัดการผู้ใช้</a>
            </div>
          </section>
          
          {/* Role Requests Panel */}
          {roleRequests.length > 0 && (
            <section className="admin-panels" style={{ marginBottom: 0 }}>
              <div className="admin-panel" style={{ width: '100%' }}>
                <h3>
                  <span><i className="fas fa-user-check"></i> คำขอเปลี่ยนบทบาท</span>
                  <span className="count-badge">{roleRequests.length}</span>
                </h3>
                <ul className="pending-list">
                  {roleRequests.map(req => (
                    <li key={req.id} className="pending-item">
                      <div className="pending-info">
                        <h4>{req.user_name || '-'}</h4>
                        <p>
                          <i className="fas fa-envelope"></i> {req.user_email || '-'} &nbsp;|&nbsp;
                          <i className="fas fa-tag"></i> ขอบทบาท: <strong>{roleLabel(req.role)}</strong>
                        </p>
                        <p><i className="far fa-calendar"></i> {req.requested_at ? new Date(req.requested_at).toLocaleDateString('th-TH') : '-'}</p>
                      </div>
                      <div className="pending-actions">
                        <button className="approve-btn" onClick={() => handleApproveRoleRequest(req.id)}>
                          <i className="fas fa-check"></i> อนุมัติ
                        </button>
                        <button className="reject-btn" onClick={() => handleRejectRoleRequest(req.id)}>
                          <i className="fas fa-times"></i> ปฏิเสธ
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/admin/role-requests')}>
                  <i className="fas fa-arrow-right"></i> ดูทั้งหมด
                </button>
              </div>
            </section>
          )}

          <section className="admin-panels">
            <div className="admin-panel analytics-cta">
              <h3><span><i className="fas fa-chart-line"></i> วิเคราะห์การใช้งาน</span></h3>
              <p>ดูสถิติการใช้งาน ระบบภาษา ประสิทธิภาพการจัดอันดับ และพฤติกรรมการค้นหา</p>
              <button className="view-btn analytics-btn" onClick={() => navigate('/admin/analytics')}>
                <i className="fas fa-chart-pie"></i> เปิดแดชบอร์ดวิเคราะห์
              </button>
            </div>

            <div className="admin-panel pending-events">
              <h3>
                <span><i className="fas fa-hourglass-half"></i> กิจกรรมที่รออนุมัติ</span>
                <span className="count-badge">{Array.isArray(pendingEvents) ? pendingEvents.length : 0}</span>
              </h3>
              
              {loading ? (
                <div className="loading">
                  <div className="loading-spinner"></div>
                  <p>กำลังโหลดข้อมูล...</p>
                </div>
              ) : Array.isArray(pendingEvents) && pendingEvents.length > 0 ? (
                <ul className="pending-list">
                  {pendingEvents.map(event => (
                    <li key={event.id} className="pending-item">
                      <div className="pending-info">
                        <h4>{event.title}</h4>
                        <p><i className="fas fa-user"></i> ส่งโดย: {event.submittedBy} | <i className="far fa-calendar"></i> วันที่: {event.date}</p>
                        <p><i className="fas fa-music"></i> ศิลปิน: {event.artist} | <i className="fas fa-map-marker-alt"></i> สถานที่: {event.venue}</p>
                      </div>
                      <div className="pending-actions">
                        <button 
                          className="approve-btn" 
                          onClick={() => handleApproveEvent(event.id)}
                        >
                          <i className="fas fa-check"></i> อนุมัติ
                        </button>
                        <button 
                          className="reject-btn" 
                          onClick={() => handleRejectEvent(event.id)}
                        >
                          <i className="fas fa-times"></i> ปฏิเสธ
                        </button>
                        <button 
                          className="view-btn" 
                          onClick={() => navigate(`/admin/events/${event.id}`)}
                        >
                          <i className="fas fa-eye"></i> ดูรายละเอียด
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon"><i className="far fa-check-circle"></i></div>
                  <p>ไม่มีกิจกรรมที่รออนุมัติในขณะนี้</p>
                </div>
              )}
              
              <button onClick={() => navigate('/admin/events/approval')}>
                <i className="fas fa-arrow-right"></i> ดูทั้งหมด
              </button>
            </div>
            
            <div className="admin-panel">
              <h3>
                <span><i className="fas fa-user-plus"></i> ผู้ใช้ล่าสุด</span>
              </h3>
              
              {loading ? (
                <div className="loading">
                  <div className="loading-spinner"></div>
                  <p>กำลังโหลดข้อมูล...</p>
                </div>
              ) : Array.isArray(recentUsers) && recentUsers.length > 0 ? (
                <ul className="user-list">
                  {recentUsers.map(user => (
                    <li key={user.id} className="user-item">
                      <div className="user-avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">{user.name ? user.name.charAt(0) : 'U'}</div>
                        )}
                      </div>
                      <div className="user-info">
                        <h4>{user.name}</h4>
                        <p><i className="fas fa-envelope"></i> อีเมล: {user.email}</p>
                        <p><i className="fas fa-clock"></i> สมัครเมื่อ: {user.createdAt}</p>
                      </div>
                      <div className="user-actions">
                        <button onClick={() => navigate(`/admin/users/${user.id}`)}>
                          <i className="fas fa-cog"></i> จัดการ
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon"><i className="far fa-user-circle"></i></div>
                  <p>ไม่มีผู้ใช้ล่าสุดในขณะนี้</p>
                </div>
              )}
              
              <button onClick={() => navigate('/admin/users')}>
                <i className="fas fa-arrow-right"></i> ดูทั้งหมด
              </button>
            </div>
          </section>
          
        </main>
      </div>
      
      <footer className="dashboard-footer">
        <p>&copy; 2025 {branding.brand?.name || 'EventZoom'}. All rights reserved. | Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminDashboard;